import { useEffect, useState } from "react";

/**
 * Subscribes to a CSS media query.
 *
 * Used to keep viewport-only UI (like the mobile preview drawer) from mounting on
 * desktop - a hidden Radix drawer still renders its full-screen dark backdrop,
 * which looked like a "black screen" flashing over the inbox.
 */
export function useMediaQuery(query) {
  const get = () =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(query).matches
      : false;

  const [matches, setMatches] = useState(get);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(mql.matches);
    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener(onChange); // Safari < 14
    return () => mql.removeListener(onChange);
  }, [query]);

  return matches;
}

/** Tailwind `xl` breakpoint - where the inbox switches to the two-pane layout. */
export const useIsDesktop = () => useMediaQuery("(min-width: 1280px)");

export default useMediaQuery;
